<template>
  <section class="col3-demo p-lg">
    <div class="col3-demo-wrap">
      <h2 v-if="titleHtml" class="col3-demo-title tt-up col3-demo-title-html" v-html="titleHtml"></h2>
      <h2 v-else-if="title" class="col3-demo-title tt-up">{{ title }}</h2>

      <div class="col3-demo-row d-f g-lg fd-r fd-cp">
        <!-- Card 1: Demo drop -->
        <div class="col3-demo-card col3-demo-card--drop bc-t d-f fd-c g m-b">
          <div class="col3-demo-panel bc-black-op-dark b-r-xl d-f fd-c g p-xl">
            <p class="tt-up mute m-0 m-b-sm">Demo drop</p>
            <button type="button" class="col3-demo-pill-btn d-f ai-c jc-c tt-up m-b-sm">
              <span class="m-r-sm">Submit demo</span>
            </button>
            <button type="button" class="col3-demo-pill-btn d-f ai-c jc-c tt-up">
              <span class="m-r-sm">Assessment</span>
            </button>
            <p class="col3-demo-footnote mute m-0 m-t-lg">.mp3, .wav (max 6MB)</p>
          </div>
          <p class="col3-demo-overline tt-up m-t-lg">Easy demo drop</p>
          <p class="col3-demo-body mute">
            Artists submit demos directly to your inbox. No more scattered links and email threads.
          </p>
        </div>

        <!-- Card 2: Rate demo -->
        <div class="col3-demo-card col3-demo-card--ratings bc-t d-f fd-c g m-b">
          <div class="col3-demo-panel bc-black-op-dark b-r-xl d-f fd-c g p-xl">
            <p class="tt-up mute m-0 m-b">Rate demo</p>
            <div
              v-for="row in ratingRows"
              :key="row.id"
              class="d-f ai-c g-half m-b-sm"
            >
              <p class="flex-1 m-0">{{ row.label }}</p>
              <div class="d-f g-half ai-c flex-1 jc-c" @mouseleave="hovering[row.id] = 0">
                <div
                  v-for="i in 5"
                  :key="i"
                  type="button"
                  class="col3-demo-star-btn p-0 bc-t"
                  @mouseover="onStarHover(row.id, i)"
                  @click="onStarClick(row.id, i)"
                >
                  <span
                    class="col3-demo-star"
                    :class="{
                      'col3-demo-star--filled':
                        i <= (hovering[row.id] || ratings[row.id] || 0),
                    }"
                  >
                    ★
                  </span>
                </div>
              </div>
              <p class="col3-demo-rating m-0">{{ formatRating(ratings[row.id]) }}</p>
            </div>

            <div class="d-f ai-c g-half m-t">
              <p class="flex-1 m-0">Summary</p>
              <div class="d-f g-half ai-c flex-1 jc-c">
                <span
                  v-for="i in 5"
                  :key="i"
                  class="col3-demo-star"
                  :class="{ 'col3-demo-star--filled': i <= summaryRatingRounded }"
                >
                  ★
                </span>
              </div>
              <p class="col3-demo-rating m-0">{{ formatRating(summaryRating) }}</p>
            </div>
          </div>

          <p class="col3-demo-overline tt-up m-t-lg">Review submissions</p>
          <p class="col3-demo-body mute">
            Your team can listen, save, reject, rate, and comment. Everyone stays aligned on every track.
          </p>
        </div>

        <!-- Card 3: Demo reviews -->
        <div class="col3-demo-card col3-demo-card--reviews bc-t d-f fd-c g m-b">
          <div class="col3-demo-panel bc-black-op-dark b-r-xl d-f fd-c g p-xl">
            <p class="tt-up mute m-0 m-b">Demo reviews</p>

            <div
              v-for="(reviewer, index) in reviewers"
              :key="reviewer.id || index"
              class="d-f ai-c g m-b-sm"
            >
              <div class="col3-demo-reviewer-avatar">
                <img
                  :src="reviewer.image"
                  :alt="reviewer.name"
                  class="col3-demo-reviewer-img"
                />
              </div>
              <div class="d-f g-half ai-c flex-1">
                <div
                  v-for="icon in reviewer.icons"
                  :key="icon.id"
                  class="tag pill-mini br-pill ai-c d-f g-half col3-demo-icon-pill"
                >
                  <span class="col3-demo-icon-symbol">{{ icon.symbol }}</span>
                  <span class="col3-demo-icon-label tt-up">{{ icon.label }}</span>
                </div>
              </div>
              <p class="col3-demo-rating m-0">{{ formatRating(reviewer.score) }}</p>
            </div>
          </div>

          <p class="col3-demo-overline tt-up m-t-lg">Track demo engagement</p>
          <p class="col3-demo-body mute">
            Submitters are provided with rich interaction data for every demo you receive.
          </p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'Col3Demo',
  props: {
    title: {
      type: String,
      default: '',
    },
    titleHtml: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      ratings: {
        overall: 4,
        creativity: 5,
        potential: 4,
        production: 3,
      },
      hovering: {
        overall: 0,
        creativity: 0,
        potential: 0,
        production: 0,
      },
      ratingRows: [
        { id: 'overall', label: 'Overall' },
        { id: 'creativity', label: 'Creativity' },
        { id: 'potential', label: 'Potential' },
        { id: 'production', label: 'Production' },
      ],
      reviewers: [
        {
          id: 'rev1',
          name: 'Reviewer 1',
          image: require('@/assets/img/features/avatars/Avatar-1.webp'),
          score: 4.3,
          icons: [
            { id: 'play', symbol: '▶', label: 'Play' },
            { id: 'like', symbol: '♥', label: 'Like' },
            { id: 'comment', symbol: '💬', label: 'Comment' },
          ],
        },
        {
          id: 'rev2',
          name: 'Reviewer 2',
          image: require('@/assets/img/features/avatars/Avatar-2.webp'),
          score: 3.5,
          icons: [
            { id: 'play', symbol: '▶', label: 'Play' },
            { id: 'save', symbol: '★', label: 'Save' },
            { id: 'share', symbol: '↗', label: 'Share' },
          ],
        },
        {
          id: 'rev3',
          name: 'Reviewer 3',
          image: require('@/assets/img/features/avatars/Avatar-3.webp'),
          score: 5.0,
          icons: [
            { id: 'play', symbol: '▶', label: 'Play' },
            { id: 'heart', symbol: '♥', label: 'Love' },
            { id: 'note', symbol: '♪', label: 'Add to list' },
          ],
        },
      ],
    }
  },
  computed: {
    summaryRating() {
      const vals = Object.values(this.ratings)
      const avg = vals.reduce((sum, v) => sum + v, 0) / vals.length
      return avg || 0
    },
    summaryRatingRounded() {
      return Math.round(this.summaryRating)
    },
  },
  methods: {
    onStarHover(id, value) {
      this.$set(this.hovering, id, value)
    },
    onStarClick(id, value) {
      this.$set(this.ratings, id, value)
    },
    formatRating(value) {
      const v = typeof value === 'number' ? value : 0
      return v.toFixed(1)
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/scss/_variables.scss";
@import "@/scss/_classes.scss";
@import "@/scss/_mixins.scss";

.col3-demo {
  @media #{$mobile} {
    padding: $mg-desktop-xl $mg-desktop;
  }
}

.col3-demo-wrap {
  margin: 0 auto;
}

.col3-demo-title {
  margin: 0 0 $mg-desktop-xl 0;
  font-size: clamp(1.5rem, 3vw, 2.25rem);
  font-weight: 500;
  color: $white;
  line-height: 1.2;
}

.col3-demo-title-html::v-deep .title-grey {
  color: $grey;
}

.col3-demo-row {
  flex-wrap: wrap;
}

.col3-demo-card {
  flex: 1 1 0;
  min-width: 260px;
  max-width: 380px;
  box-sizing: border-box;
}

.col3-demo-card--drop .col3-demo-panel,
.col3-demo-card--ratings .col3-demo-panel,
.col3-demo-card--reviews .col3-demo-panel {
  background: radial-gradient(circle at top, #383838 0, #181818 45%, #121212 100%);
}

.col3-demo-panel {
  border-radius: $border-radius-xl;
  border: 1px solid rgba(255, 255, 255, 0.06);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.6);
}

.col3-demo-pill-btn {
  width: 100%;
  padding: $mg-desktop-md $mg-desktop-xl;
  border-radius: $border-radius-pill;
  border: 1px solid rgba(255, 255, 255, 0.6);
  background: transparent;
  color: $white;
  font-size: 0.875rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: $white;
    color: $black;
    border-color: $white;
  }
}

.col3-demo-footnote {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.6);
}

.col3-demo-overline {
  margin: 0 0 $mg-desktop-sm 0;
  font-size: 0.75rem;
  letter-spacing: 0.14em;
}

.col3-demo-body {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.75);
}

.col3-demo-star-btn {
  border: none;
  background: none;
  cursor: pointer;
}

.col3-demo-star {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.25);
  transition: color 0.15s ease;

  &--filled {
    color: $white;
  }
}

.col3-demo-rating {
  min-width: 2.5rem;
  text-align: right;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.9);
}

.col3-demo-reviewer-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
}

.col3-demo-reviewer-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.col3-demo-icon-pill {
  max-width: 48px;
  overflow: hidden;
  transition: max-width 0.2s ease, background 0.2s ease, border-color 0.2s ease;

  &:hover {
    max-width: 160px;
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.3);
  }
}

.col3-demo-icon-symbol {
  font-size: 0.75rem;
}

.col3-demo-icon-label {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.9);
  white-space: nowrap;
}
</style>

